/**
 * "My documents" — every file that belongs to the CALLER, in one list: documents HR shared with them
 * through a document task (and the signed copy they sent back), and the files they uploaded for
 * their own onboarding checklist.
 *
 * READ-ONLY AND OWN-ONLY. There is no `personId` parameter: the list is always the caller's, so
 * there is nothing to authorise per row and nothing to widen. Completing a pending document task is
 * NOT a new route — it is document-tasks.contract.ts's `complete` (plus its upload ticket), which
 * already refuses anyone but the task's own person.
 *
 * Hidden from the AI copilot (`ai: 'hidden'`): every `fileUrl` here is a bearer link to the file
 * itself, and the files are exactly the ones that carry PII (an Aadhaar or PAN scan uploaded during
 * onboarding, a signed offer letter). The copilot can navigate someone to the page; it never pulls
 * the links into a prompt.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { dateOnlySchema, errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/**
 * Where a file came from:
 *   - `shared_with_me` — the document HR assigned in a document task (a policy, a letter to sign);
 *   - `signed_copy`    — the signed copy the caller returned for that task;
 *   - `onboarding`     — a file the caller uploaded for one of their own onboarding tasks.
 */
export const myDocumentSourceSchema = z.enum(['shared_with_me', 'signed_copy', 'onboarding']);
export type MyDocumentSource = z.infer<typeof myDocumentSourceSchema>;

export const myDocumentSchema = z.object({
  /** Stable per file: `<source>:<row id>` — one task yields up to two files. */
  id: z.string(),
  source: myDocumentSourceSchema,
  title: z.string(),
  description: z.string().nullable(),
  /** Open or download the file. A bearer link: never log it or hand it to the copilot. */
  fileUrl: z.string().url(),
  /** The document task this file belongs to, for `shared_with_me` and `signed_copy`. */
  documentTaskId: uuidSchema.nullable(),
  /** For `shared_with_me`: whether the task behind it is still waiting on the caller. */
  taskStatus: z.enum(['pending', 'completed']).nullable(),
  /** For `shared_with_me`: the task's due date. */
  dueDate: dateOnlySchema.nullable(),
  /** Who shared it (`shared_with_me`), or null for the caller's own uploads. */
  sharedByName: z.string().nullable(),
  /** When the file was shared, returned, or uploaded. */
  addedAt: instantSchema,
});
export type MyDocument = z.infer<typeof myDocumentSchema>;

export const myDocumentsContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/documents',
      responses: {
        200: z.array(myDocumentSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        "The caller's own documents: files HR shared with them, signed copies they returned, and their onboarding uploads",
      metadata: routeMeta('employee.document:read', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/me' },
);
